import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import PaladinLogo from "../../svg/paladin-logo.svg";
import HeroImg from "../../assets/images/paladinhero.png";

export default function AdminLogin() {
  usePageTitle("Admin Login");
  const navigate = useNavigate();
  const [form, setForm]         = useState({ username: "", password: "" });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [showPass, setShowPass] = useState(false);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

   const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Login failed.");
      localStorage.setItem("paladin_admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }; 

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      backgroundColor: "#FFFFFF",
    }}>

      {/* ── Left panel ── */}
      <div style={{
        width: "420px",
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Hero image */}
        <img
          src={HeroImg}
          alt=""
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            objectFit: "cover", objectPosition: "center",
          }}
        />

        {/* Blue overlay */}
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(160deg, rgba(1,46,114,0.93) 0%, rgba(0,26,74,0.97) 80%)",
        }} />

        {/* Content above overlay */}
        <div style={{
          position: "relative", zIndex: 1,
          display: "flex", flexDirection: "column",
          flex: 1, padding: "48px 44px",
        }}>

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "auto" }}>
          <img src={PaladinLogo} alt="Paladin Logo" style={{ width: "38px", height: "38px", objectFit: "contain", flexShrink: 0, filter: "brightness(0) invert(1)" }} />
          <div>
            <p style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "16px", letterSpacing: "0.08em", color: "#fff", lineHeight: "1.1" }}>PALADIN</p>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginTop: "2px" }}>Insurance Admin</p>
          </div>
        </div>

        {/* Tagline block */}
        <div style={{ marginBottom: "48px" }}>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", marginBottom: "14px" }}>
            Authorized Personnel Only
          </p>
          <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "26px", color: "#fff", lineHeight: 1.3, letterSpacing: "0.03em", marginBottom: "16px" }}>
            Welcome back to the admin portal.
          </h2>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "rgba(255,255,255,0.45)", lineHeight: 1.8 }}>
            Manage quotes, consultations, messages, blog posts, and client data — all in one secure place.
          </p>
        </div>

        {/* Bottom note */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(255,255,255,0.28)" }}>
            Sessions expire in 8 hours
          </p>
        </div>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "48px 32px",
        backgroundColor: "#F7F4EF",
      }}>
        <div style={{ width: "100%", maxWidth: "380px" }}>

          {/* Heading */}
          <div style={{ marginBottom: "36px" }}>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "8px" }}>
              Admin Portal
            </p>
            <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.04em", color: "#012E72", lineHeight: 1 }}>
              Sign In
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

            {/* Username */}
            <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <label style={{ fontFamily: "'Cinzel', serif", fontSize: "8.5px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)" }}>
                Username
              </label>
              <input
                type="text" name="username" required autoComplete="username"
                placeholder="Enter username"
                value={form.username} onChange={handleChange}
                style={{
                  fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72",
                  backgroundColor: "#fff", border: "1px solid rgba(1,46,114,0.14)",
                  borderRadius: "12px", padding: "12px 16px", outline: "none",
                  transition: "border-color 0.15s",
                }}
                onFocus={e => e.target.style.borderColor = "#002DB5"}
                onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.14)"}
              />
            </div>

            {/* Password */}
            <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <label style={{ fontFamily: "'Cinzel', serif", fontSize: "8.5px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.4)" }}>
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPass ? "text" : "password"} name="password" required
                  autoComplete="current-password"
                  placeholder="Enter password"
                  value={form.password} onChange={handleChange}
                  style={{
                    width: "100%", fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72",
                    backgroundColor: "#fff", border: "1px solid rgba(1,46,114,0.14)",
                    borderRadius: "12px", padding: "12px 44px 12px 16px", outline: "none",
                    transition: "border-color 0.15s", boxSizing: "border-box",
                  }}
                  onFocus={e => e.target.style.borderColor = "#002DB5"}
                  onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.14)"}
                />
                <button
                  type="button" onClick={() => setShowPass(v => !v)}
                  style={{
                    position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)",
                    background: "none", border: "none", cursor: "pointer",
                    color: "rgba(1,46,114,0.35)", transition: "color 0.15s", padding: 0,
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = "#012E72"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(1,46,114,0.35)"}
                >
                  {showPass ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: "10px",
                padding: "12px 14px", borderRadius: "10px",
                backgroundColor: "#FEF2F2", border: "1px solid rgba(220,38,38,0.2)",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#dc2626" }}>{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit" disabled={loading}
              style={{
                fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.2em",
                textTransform: "uppercase", color: "#fff",
                background: "linear-gradient(135deg, #012E72, #002DB5)",
                border: "none", borderRadius: "12px",
                padding: "14px", cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.7 : 1,
                boxShadow: loading ? "none" : "0 4px 20px rgba(0,45,181,0.3)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                transition: "opacity 0.15s, box-shadow 0.15s",
                marginTop: "4px",
              }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                  Signing In…
                </>
              ) : "Sign In"}
            </button>
          </form>

          {/* Footer note */}
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.3)", fontStyle: "italic", textAlign: "center", marginTop: "28px" }}>
            Unauthorized access attempts are logged and rate-limited.
          </p>
        </div>
      </div>
    </div>
  );
}